import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Lock, Unlock, Eye, EyeOff, Trash2, Copy, Image as ImageIcon } from 'lucide-react';
import {
  CanvasElement as CanvasElementType,
  CanvasElementType as ElementType,
  TextCanvasElement,
  VariableCanvasElement,
  ImageCanvasElement,
  ShapeCanvasElement,
  LineCanvasElement,
  QRCodeCanvasElement,
  SignatureCanvasElement
} from '@/types/certificate-template.types';

interface CanvasElementProps {
  element: CanvasElementType;
  isSelected: boolean;
  scale: number;
  onSelect: (id: string) => void;
  onUpdate: (id: string, updates: Partial<CanvasElementType>) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  sampleData?: Record<string, string>;
}

interface ResizeHandle {
  position: 'nw' | 'n' | 'ne' | 'w' | 'e' | 'sw' | 's' | 'se';
  cursor: string;
}

const resizeHandles: ResizeHandle[] = [
  { position: 'nw', cursor: 'nwse-resize' },
  { position: 'n', cursor: 'ns-resize' },
  { position: 'ne', cursor: 'nesw-resize' },
  { position: 'w', cursor: 'ew-resize' },
  { position: 'e', cursor: 'ew-resize' },
  { position: 'sw', cursor: 'nesw-resize' },
  { position: 's', cursor: 'ns-resize' },
  { position: 'se', cursor: 'nwse-resize' }
];

export default function CanvasElement({
  element,
  isSelected,
  scale,
  onSelect,
  onUpdate,
  onDelete,
  onDuplicate,
  sampleData: _sampleData = {}
}: CanvasElementProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0, width: 0, height: 0 });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (element.locked) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect(element.id);

    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialPosition({
      x: element.position.x,
      y: element.position.y,
      width: element.position.width,
      height: element.position.height
    });
  }, [element, onSelect]);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent, handle: string) => {
    if (element.locked) return;
    e.stopPropagation();
    e.preventDefault();

    setIsResizing(true);
    setResizeHandle(handle);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialPosition({
      x: element.position.x,
      y: element.position.y,
      width: element.position.width,
      height: element.position.height
    });
  }, [element]);

  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = (e.clientX - dragStart.x) / scale;
      const deltaY = (e.clientY - dragStart.y) / scale;

      if (isDragging) {
        onUpdate(element.id, {
          position: {
            ...element.position,
            x: Math.max(0, initialPosition.x + deltaX),
            y: Math.max(0, initialPosition.y + deltaY)
          }
        });
      } else if (isResizing && resizeHandle) {
        let newX = initialPosition.x;
        let newY = initialPosition.y;
        let newWidth = initialPosition.width;
        let newHeight = initialPosition.height;

        if (resizeHandle.includes('w')) {
          newWidth = Math.max(50, initialPosition.width - deltaX);
          newX = initialPosition.x + (initialPosition.width - newWidth);
        }
        if (resizeHandle.includes('e')) {
          newWidth = Math.max(50, initialPosition.width + deltaX);
        }
        if (resizeHandle.includes('n')) {
          newHeight = Math.max(20, initialPosition.height - deltaY);
          newY = initialPosition.y + (initialPosition.height - newHeight);
        }
        if (resizeHandle.includes('s')) {
          newHeight = Math.max(20, initialPosition.height + deltaY);
        }

        onUpdate(element.id, {
          position: {
            ...element.position,
            x: newX,
            y: newY,
            width: newWidth,
            height: newHeight
          }
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, initialPosition, resizeHandle, scale, element, onUpdate]);

  const renderContent = () => {
    switch (element.type) {
      case ElementType.TEXT: {
        const textEl = element as TextCanvasElement;
        return (
          <div
            style={{
              fontFamily: textEl.style.fontFamily,
              fontSize: `${textEl.style.fontSize}px`,
              fontWeight: textEl.style.fontWeight,
              fontStyle: textEl.style.fontStyle,
              textDecoration: textEl.style.textDecoration,
              textAlign: textEl.style.textAlign,
              lineHeight: textEl.style.lineHeight,
              letterSpacing: `${textEl.style.letterSpacing}px`,
              color: textEl.style.color,
              backgroundColor: textEl.style.backgroundColor || 'transparent',
              padding: textEl.style.padding ? `${textEl.style.padding}px` : undefined,
              borderRadius: textEl.style.borderRadius ? `${textEl.style.borderRadius}px` : undefined,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: textEl.style.textAlign === 'center' ? 'center' :
                textEl.style.textAlign === 'right' ? 'flex-end' : 'flex-start',
              overflow: 'visible',
              whiteSpace: 'nowrap'
            }}
          >
            {textEl.content}
          </div>
        );
      }

      case ElementType.VARIABLE: {
        const varEl = element as VariableCanvasElement;
        return (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: varEl.style.textAlign === 'center' ? 'center' :
                varEl.style.textAlign === 'right' ? 'flex-end' : 'flex-start',
              overflow: 'visible'
            }}
          >
            <span
              style={{
                // Dynamic Styles from Properties Panel
                color: varEl.style.color, 
                fontFamily: varEl.style.fontFamily,
                fontSize: `${varEl.style.fontSize}px`,
                fontWeight: varEl.style.fontWeight,
                fontStyle: varEl.style.fontStyle,
                textDecoration: varEl.style.textDecoration,
                letterSpacing: `${varEl.style.letterSpacing}px`,
                
                // Variable Indicator
                backgroundColor: 'rgba(59, 130, 246, 0.05)', 
                border: '1px dashed #3B82F6', // Keep dashed border to identify as variable
                borderRadius: '4px',
                padding: '2px 6px',
                
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 1
              }}
              title={`{{${varEl.variableKey}}}`}
            >
              {`{{${varEl.variableKey}}}`}
            </span>
          </div>
        );
      }

      case ElementType.IMAGE: {
        const imgEl = element as ImageCanvasElement;
        // No picture chosen yet: show an "add image" box, not a broken image with a label
        if (!imgEl.src) {
          return (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1 border border-dashed border-gray-300 bg-gray-50 text-gray-400 text-xs select-none">
              <ImageIcon className="w-5 h-5" />
              Upload an image
            </div>
          );
        }
        return (
          <img
            src={imgEl.src}
            alt={imgEl.alt || 'Certificate image'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: imgEl.fit,
              borderRadius: imgEl.border?.radius ? `${imgEl.border.radius}px` : undefined
            }}
            draggable={false}
          />
        );
      }

      case ElementType.SHAPE: {
        const shapeEl = element as ShapeCanvasElement;
        const borderRadius = shapeEl.shapeType === 'circle' ? '50%' :
          shapeEl.shapeType === 'ellipse' ? '50%' :
            shapeEl.border?.radius ? `${shapeEl.border.radius}px` : undefined;

        return (
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: shapeEl.fill,
              borderRadius,
              border: shapeEl.border ?
                `${shapeEl.border.width}px ${shapeEl.border.style} ${shapeEl.border.color}` :
                undefined
            }}
          />
        );
      }

      case ElementType.LINE: {
        const lineEl = element as LineCanvasElement;
        const w = element.position?.width ?? 100;
        const h = element.position?.height ?? 10;
        // Derive orientation from element dimensions — vertical when taller than wide
        const isVertical = h > w;
        const sw = lineEl.strokeWidth || 2;
        return (
          <svg
            width="100%"
            height="100%"
            style={{ position: 'absolute', top: 0, left: 0 }}
          >
            <line
              x1={isVertical ? sw / 2 : 0}
              y1={isVertical ? 0 : sw / 2}
              x2={isVertical ? sw / 2 : w}
              y2={isVertical ? h : sw / 2}
              stroke={lineEl.strokeColor || '#000000'}
              strokeWidth={sw}
              strokeDasharray={
                lineEl.strokeStyle === 'dashed' ? '10,5' :
                  lineEl.strokeStyle === 'dotted' ? '2,2' : undefined
              }
            />
          </svg>
        );
      }

      case ElementType.QR_CODE: {
        const qrEl = element as QRCodeCanvasElement;
        return (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: qrEl.backgroundColor
            }}
          >
            <div
              style={{
                width: qrEl.size,
                height: qrEl.size,
                backgroundColor: '#f0f0f0',
                border: '1px dashed #999',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                textAlign: 'center',
                color: '#666'
              }}
            >
              QR Code
              <br />
              (Preview)
            </div>
          </div>
        );
      }

      case ElementType.SIGNATURE: {
        const sigEl = element as SignatureCanvasElement;
        return (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {sigEl.signatureType === 'text' && sigEl.text && (
              <div
                style={{
                  fontFamily: sigEl.style?.fontFamily || '"Dancing Script", cursive',
                  fontSize: `${sigEl.style?.fontSize || 24}px`,
                  color: sigEl.style?.color || '#111827'
                }}
              >
                {sigEl.text}
              </div>
            )}
            {sigEl.signatureType === 'image' && sigEl.src && (
              <img
                src={sigEl.src}
                alt="Signature"
                style={{ maxWidth: '100%', maxHeight: '70%' }}
                draggable={false}
              />
            )}
            {sigEl.showLabel && sigEl.label && (
              <div
                style={{
                  marginTop: '4px',
                  fontSize: '11px',
                  color: '#6B7280',
                  borderTop: '1px solid #E5E7EB',
                  paddingTop: '4px'
                }}
              >
                {sigEl.label}
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  if (!element.visible) return null;

  return (
    <div
      ref={elementRef}
      className={`absolute group ${element.locked ? 'cursor-not-allowed' : 'cursor-move'}`}
      style={{
        left: element.position.x,
        top: element.position.y,
        width: element.position.width,
        height: element.position.height,
        transform: element.position.rotation ? `rotate(${element.position.rotation}deg)` : undefined,
        zIndex: element.position.zIndex || 1,
        opacity: element.opacity ?? 1,
        outline: isSelected ? '2px solid #3B82F6' : undefined,
        outlineOffset: '2px'
      }}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(element.id);
      }}
    >
      {renderContent()}

      {/* Selection overlay and controls */}
      {isSelected && (
        <>
          {/* Resize handles */}
          {!element.locked && resizeHandles.map((handle) => (
            <div
              key={handle.position}
              className={`absolute w-3 h-3 bg-white border-2 border-blue-500 rounded-sm z-10
                ${handle.position.includes('n') ? 'top-0 -translate-y-1/2' : ''}
                ${handle.position.includes('s') ? 'bottom-0 translate-y-1/2' : ''}
                ${handle.position.includes('w') ? 'left-0 -translate-x-1/2' : ''}
                ${handle.position.includes('e') ? 'right-0 translate-x-1/2' : ''}
                ${handle.position === 'n' || handle.position === 's' ? 'left-1/2 -translate-x-1/2' : ''}
                ${handle.position === 'w' || handle.position === 'e' ? 'top-1/2 -translate-y-1/2' : ''}
              `}
              style={{ cursor: handle.cursor }}
              onMouseDown={(e) => handleResizeMouseDown(e, handle.position)}
            />
          ))}

          {/* Quick actions toolbar */}
          <div className="absolute -top-10 left-0 flex items-center gap-1 bg-white rounded-lg shadow-lg border px-1 py-0.5 z-20">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpdate(element.id, { locked: !element.locked });
              }}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title={element.locked ? 'Unlock' : 'Lock'}
            >
              {element.locked ? (
                <Lock className="w-3.5 h-3.5 text-amber-500" />
              ) : (
                <Unlock className="w-3.5 h-3.5 text-gray-500" />
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpdate(element.id, { visible: !element.visible });
              }}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title={element.visible ? 'Hide' : 'Show'}
            >
              {element.visible ? (
                <Eye className="w-3.5 h-3.5 text-gray-500" />
              ) : (
                <EyeOff className="w-3.5 h-3.5 text-gray-400" />
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(element.id);
              }}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Duplicate"
            >
              <Copy className="w-3.5 h-3.5 text-gray-500" />
            </button>
            <div className="w-px h-4 bg-gray-200 mx-0.5" />
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(element.id);
              }}
              className="p-1.5 hover:bg-red-50 rounded transition-colors"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5 text-red-500" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
